import { SW_BRAND, SW_EMAIL, SW_PHONE_DISPLAY } from './config';

export const SW_HERO = {
  badge: 'Unlock Better Airfares on Every Journey',
  titleLead: 'Search Affordable Tickets for',
  title: 'Domestic Flights',
  chips: [
    { icon: 'deals', label: 'Late-Availability Fares', sub: 'Savings of up to 50%' },
    { icon: 'help', label: 'Dedicated Travel Support', sub: 'Available day and night' },
    { icon: 'book', label: 'Bookings, Fees & Cancellations', sub: 'Clear, flexible rules' },
  ],
};

export const SW_ARTICLE = {
  title: 'Southwest Airlines: a practical guide to lower-cost travel',
  intro:
    'Southwest Airlines built its reputation on straightforward service, included checked bags, and competitive domestic pricing. As a major U.S. carrier, it links travellers to cities nationwide and to selected destinations in Mexico, Central America, and the Caribbean.',
  sections: [
    {
      id: 'swp-baggage',
      heading: 'How Southwest Airlines handles baggage',
      paragraphs: [
        'Included bags remain one of Southwest’s strongest selling points. Travellers typically receive two free checked bags (up to 50 lbs each), plus a free carry-on and personal item. That alone can reduce a round-trip cost by roughly $100 compared with airlines that charge for hold luggage.',
      ],
    },
    {
      id: 'swp-routes',
      heading: 'Popular Southwest routes and destinations',
      paragraphs: [
        'The network covers more than 100 cities across 40 U.S. states, with additional service to Mexico, Central America, and the Caribbean. Frequently searched city pairs include:',
      ],
      bullets: [
        'Las Vegas (LAS) – Los Angeles (LAX): among the highest-traffic corridors',
        'Orlando (MCO) – Atlanta (ATL): a favourite for family leisure trips',
        'Denver (DEN) – Chicago Midway (MDW): strong for both business and leisure',
        'Nashville (BNA) – Dallas Love Field (DAL): reliable Music City connections',
        'Near-international leisure: Cancun, Cabo San Lucas, Montego Bay, and Belize City',
      ],
    },
    {
      id: 'swp-rewards',
      heading: 'Rapid Rewards loyalty programme',
      paragraphs: [
        'Rapid Rewards is widely regarded as one of the stronger U.S. airline loyalty schemes. Points are based on the amount you spend rather than distance flown, they do not expire, and award travel is free of blackout dates. Eligible members can also unlock a Companion Pass so a named companion travels for taxes and fees only.',
      ],
    },
    {
      id: 'swp-tips',
      heading: 'Practical ways to book Southwest for less',
      paragraphs: ['These habits often help travellers secure a better Southwest fare:'],
      bullets: [
        'Compare Tuesday and Wednesday departures, which are often priced lower',
        'Check the Low Fare Calendar to spot the cheapest travel window',
        'Set fare alerts so you hear about price drops as they happen',
        `Use ${SW_BRAND} to review options and speak with support any time`,
        'Consider EarlyBird Check-In when a better boarding position matters',
      ],
    },
    {
      id: 'swp-changes',
      heading: 'Changing or cancelling a Southwest flight',
      paragraphs: [
        'Southwest’s change rules are more flexible than most. There is no flat change fee — you settle only any difference in fare. If you cancel, the ticket value typically returns as travel credit that does not expire, which is why many travellers prefer Southwest when plans may shift.',
      ],
    },
    {
      id: 'swp-contact',
      heading: `Booking Southwest with ${SW_BRAND}`,
      paragraphs: [
        `Reserving Southwest flights through ${SW_BRAND} means more than a ticket confirmation. You can compare dates, review current options, and book with clearer next steps. Our support team stays available around the clock for changes, cancellations, and trip questions.`,
      ],
    },
  ],
};

export const SW_FAQ_INTRO =
  'Clear answers to frequent questions about Southwest flights, bags, and ticket rules';

export const SW_FAQS = [
  {
    q: 'What bags are included on Southwest Airlines?',
    a: 'Southwest typically includes two free checked bags per passenger (up to 50 lbs each). Carry-on bags and personal items are also free, which makes the allowance more generous than many competitors.',
  },
  {
    q: 'When and how can I check in for a Southwest flight?',
    a: 'Online check-in usually opens 24 hours before departure. You can also use airport kiosks or the ticket counter. Mobile boarding passes are available through the Southwest app.',
  },
  {
    q: 'Am I able to change or cancel my Southwest booking?',
    a: 'Yes. Southwest allows changes and cancellations without a flat change fee. Any fare difference still applies on changes, and cancelled tickets are commonly returned as travel credit that does not expire.',
  },
  {
    q: 'Where does Southwest Airlines fly?',
    a: 'Southwest serves more than 100 destinations across the United States, Mexico, Central America, and the Caribbean. Busy markets include Las Vegas, Orlando, Chicago, Denver, and Los Angeles.',
  },
  {
    q: 'Does Southwest offer a frequent-flyer programme?',
    a: 'Yes — Rapid Rewards. Earn points on eligible travel, redeem for future flights, and enjoy benefits such as no blackout dates and points that do not expire.',
  },
  {
    q: 'How does seating work on Southwest?',
    a: 'Southwest uses open seating. Boarding groups (A, B, and C) are assigned based on check-in order. EarlyBird Check-In can help you secure an earlier boarding position.',
  },
  {
    q: 'Does Southwest operate international flights?',
    a: 'Yes. Southwest flies to destinations in Mexico (including Cancun, Cabo San Lucas, and Puerto Vallarta), Central America (such as Belize and Costa Rica), and the Caribbean (including the Bahamas, Jamaica, and Grand Cayman).',
  },
  {
    q: 'How do I reach Southwest Airlines customer service?',
    a: `For airline-specific help, contact Southwest at 1-800-I-FLY-SWA (1-800-435-9792). For booking questions with ${SW_BRAND}, call ${SW_PHONE_DISPLAY} or email ${SW_EMAIL}.`,
  },
];

export const SW_AIRLINES = [
  { code: 'J', name: 'JetBlue Airways', note: 'From $79' },
  { code: 'A', name: 'Alaska Airlines', note: 'From $99' },
  { code: 'H', name: 'Hawaiian Airlines', note: 'From $89' },
  { code: 'A', name: 'American Airlines', note: 'From $85' },
  { code: 'S', name: 'Southwest Airlines', note: 'From $49' },
  { code: 'C', name: 'Caribbean Airlines', note: 'From $49' },
];

const JETBLUE_HERO = {
  ...SW_HERO,
  titleLead: 'Search Affordable JetBlue Tickets for',
  title: 'Domestic & Caribbean Flights',
};

const JETBLUE_ARTICLE = {
  title: 'JetBlue Airways: a practical guide to booking smarter',
  intro:
    'JetBlue Airways is known for roomy economy seats, free in-flight Wi-Fi, and its Mint premium cabin. From focus cities such as New York (JFK), Boston, Fort Lauderdale, and Orlando, it connects travellers across the United States, the Caribbean, Latin America, and selected cities in Europe.',
  sections: [
    {
      id: 'swp-baggage',
      heading: 'How JetBlue handles baggage',
      paragraphs: [
        'JetBlue fares come in bundles such as Blue Basic, Blue, Blue Extra, and Mint, and what is included depends on the bundle you choose. Checked bags are generally charged on economy fares, with the fee depending on the route and how early you pay. Always confirm the current allowance for your fare before you fly.',
      ],
    },
    {
      id: 'swp-routes',
      heading: 'Popular JetBlue routes and destinations',
      paragraphs: [
        'JetBlue is especially strong on the East Coast and to leisure destinations in the Caribbean. Frequently searched city pairs include:',
      ],
      bullets: [
        'New York (JFK) – Los Angeles (LAX): a transcontinental favourite, with Mint on many flights',
        'Boston (BOS) – Fort Lauderdale (FLL): a popular winter getaway route',
        'New York (JFK) – San Juan (SJU): one of the busiest Caribbean corridors',
        'Orlando (MCO) – New York (JFK): busy year-round for families',
        'Transatlantic service to cities such as London, Paris, Amsterdam, and Dublin',
      ],
    },
    {
      id: 'swp-rewards',
      heading: 'TrueBlue loyalty programme',
      paragraphs: [
        'TrueBlue members earn points based on the fare paid, and points do not expire. Points can be redeemed for any available seat with no blackout dates, and frequent flyers can work towards Mosaic status for extra perks. Families and friends can also pool points together.',
      ],
    },
    {
      id: 'swp-tips',
      heading: 'Practical ways to book JetBlue for less',
      paragraphs: ['These habits often help travellers secure a better JetBlue fare:'],
      bullets: [
        'Compare midweek departures, which are often priced lower',
        'Use the Best Fare Finder calendar to spot the cheapest dates',
        'Check whether Blue Basic restrictions suit your trip before choosing it',
        `Use ${SW_BRAND} to review options and speak with support any time`,
        'Consider Even More Space seats when legroom and early boarding matter',
      ],
    },
    {
      id: 'swp-changes',
      heading: 'Changing or cancelling a JetBlue flight',
      paragraphs: [
        'On most JetBlue fares other than Blue Basic, there is no change fee — you pay only any difference in fare, and cancelled tickets usually return as JetBlue travel credit. Blue Basic fares are more restrictive, so check the rules for your ticket before making changes.',
      ],
    },
    {
      id: 'swp-contact',
      heading: `Booking JetBlue with ${SW_BRAND}`,
      paragraphs: [
        `Reserving JetBlue flights through ${SW_BRAND} means more than a ticket confirmation. You can compare dates, review current options, and book with clearer next steps. Our support team stays available around the clock for changes, cancellations, and trip questions.`,
      ],
    },
  ],
};

const JETBLUE_FAQ_INTRO =
  'Clear answers to frequent questions about JetBlue flights, bags, and ticket rules';

const JETBLUE_FAQS = [
  {
    q: 'What bags are included on JetBlue?',
    a: 'It depends on your fare. A personal item is included on all fares, and carry-on and checked bag allowances vary by fare bundle and route. Checked bags are usually charged on economy fares, so check the latest JetBlue bag rules before you travel.',
  },
  {
    q: 'When and how can I check in for a JetBlue flight?',
    a: 'Online check-in usually opens 24 hours before departure through the JetBlue website or app. You can also check in at airport kiosks or the ticket counter.',
  },
  {
    q: 'Am I able to change or cancel my JetBlue booking?',
    a: 'Most fares other than Blue Basic can be changed or cancelled without a change fee. Any fare difference still applies, and cancelled tickets are commonly returned as JetBlue travel credit.',
  },
  {
    q: 'Where does JetBlue fly?',
    a: 'JetBlue serves cities across the United States, the Caribbean, Mexico, Central and South America, and selected cities in Europe. Busy markets include New York, Boston, Fort Lauderdale, Orlando, and San Juan.',
  },
  {
    q: 'Does JetBlue offer a frequent-flyer programme?',
    a: 'Yes — TrueBlue. Earn points on eligible travel and purchases, redeem them for any available seat, and enjoy points that do not expire.',
  },
  {
    q: 'What is JetBlue Mint?',
    a: 'Mint is JetBlue’s premium cabin, offered on many transcontinental and international routes. It features lie-flat seats, with private suites on many aircraft.',
  },
  {
    q: 'Does JetBlue have free Wi-Fi?',
    a: 'JetBlue offers free Fly-Fi internet on its aircraft, along with seatback entertainment on most flights.',
  },
  {
    q: 'How do I reach JetBlue customer service?',
    a: `For airline-specific help, contact JetBlue at 1-800-JETBLUE (1-800-538-2583). For booking questions with ${SW_BRAND}, call ${SW_PHONE_DISPLAY} or email ${SW_EMAIL}.`,
  },
];

const ALASKA_HERO = {
  ...SW_HERO,
  titleLead: 'Search Affordable Alaska Airlines Tickets for',
  title: 'West Coast & Beyond',
};

const ALASKA_ARTICLE = {
  title: 'Alaska Airlines: a practical guide to lower-cost travel',
  intro:
    'Alaska Airlines is a major U.S. carrier with its main hub in Seattle and a strong network along the West Coast, across Alaska, and to Hawaii, Mexico, and Costa Rica. As a oneworld alliance member, it also connects travellers to partner flights around the world.',
  sections: [
    {
      id: 'swp-baggage',
      heading: 'How Alaska Airlines handles baggage',
      paragraphs: [
        'Alaska Airlines includes one carry-on bag and one personal item on its fares. Checked bags are usually charged, although some cardholders and elite members get bags included. Alaska is also known for its baggage service guarantee, which promises your bags reach the carousel quickly after arrival.',
      ],
    },
    {
      id: 'swp-routes',
      heading: 'Popular Alaska Airlines routes and destinations',
      paragraphs: [
        'Alaska serves more than 100 destinations, with hubs and key cities including Seattle, Portland, Anchorage, San Francisco, and Los Angeles. Frequently searched city pairs include:',
      ],
      bullets: [
        'Seattle (SEA) – Anchorage (ANC): the main gateway into Alaska',
        'Seattle (SEA) – Los Angeles (LAX): a busy West Coast business route',
        'San Francisco (SFO) – Seattle (SEA): frequent daily departures',
        'West Coast cities to Honolulu (HNL), Maui (OGG), and Kona (KOA)',
        'Leisure trips to Mexico and Costa Rica, including Cabo San Lucas and Puerto Vallarta',
      ],
    },
    {
      id: 'swp-rewards',
      heading: 'Alaska Airlines loyalty programme',
      paragraphs: [
        'Alaska’s loyalty programme, now called Atmos Rewards, lets members earn and redeem points on Alaska, Hawaiian Airlines, and oneworld partner flights. Members can earn elite status for perks such as upgrades and free checked bags, and Alaska cardholders can receive an annual Companion Fare.',
      ],
    },
    {
      id: 'swp-tips',
      heading: 'Practical ways to book Alaska Airlines for less',
      paragraphs: ['These habits often help travellers secure a better Alaska fare:'],
      bullets: [
        'Compare Tuesday and Wednesday departures, which are often priced lower',
        'Use the Low Fare Calendar to spot the cheapest travel window',
        'Check whether Saver fare restrictions suit your trip before choosing it',
        `Use ${SW_BRAND} to review options and speak with support any time`,
        'Look at partner flights for longer international journeys',
      ],
    },
    {
      id: 'swp-changes',
      heading: 'Changing or cancelling an Alaska Airlines flight',
      paragraphs: [
        'Main Cabin and First Class fares on Alaska can usually be changed or cancelled without a change fee — you pay only any difference in fare, and cancelled tickets typically return as travel credit. Saver fares are more restrictive, so check the rules for your ticket before making changes.',
      ],
    },
    {
      id: 'swp-contact',
      heading: `Booking Alaska Airlines with ${SW_BRAND}`,
      paragraphs: [
        `Reserving Alaska Airlines flights through ${SW_BRAND} means more than a ticket confirmation. You can compare dates, review current options, and book with clearer next steps. Our support team stays available around the clock for changes, cancellations, and trip questions.`,
      ],
    },
  ],
};

const ALASKA_FAQ_INTRO =
  'Clear answers to frequent questions about Alaska Airlines flights, bags, and ticket rules';

const ALASKA_FAQS = [
  {
    q: 'What bags are included on Alaska Airlines?',
    a: 'Alaska includes one carry-on bag and one personal item. Checked bags are usually charged, but eligible cardholders and elite members may get bags included. Check the latest Alaska bag rules before you travel.',
  },
  {
    q: 'When and how can I check in for an Alaska Airlines flight?',
    a: 'Online check-in usually opens 24 hours before departure through the Alaska website or app. You can also check in at airport kiosks or the ticket counter.',
  },
  {
    q: 'Am I able to change or cancel my Alaska Airlines booking?',
    a: 'Main Cabin and First Class fares can usually be changed or cancelled without a change fee. Any fare difference still applies. Saver fares have stricter rules.',
  },
  {
    q: 'Where does Alaska Airlines fly?',
    a: 'Alaska serves more than 100 destinations across the United States, plus Hawaii, Mexico, Costa Rica, and Canada. Partner airlines in the oneworld alliance extend the network worldwide.',
  },
  {
    q: 'Does Alaska Airlines offer a frequent-flyer programme?',
    a: 'Yes — Atmos Rewards (formerly Mileage Plan). Earn points on Alaska, Hawaiian, and partner flights, and redeem them for travel across the network.',
  },
  {
    q: 'Is Alaska Airlines part of an alliance?',
    a: 'Yes. Alaska is a member of the oneworld alliance, alongside airlines such as American Airlines, British Airways, and Qantas.',
  },
  {
    q: 'Does Alaska Airlines fly to Hawaii?',
    a: 'Yes. Alaska flies to Hawaii from many West Coast cities, and its network now also includes Hawaiian Airlines flights.',
  },
  {
    q: 'How do I reach Alaska Airlines customer service?',
    a: `For airline-specific help, contact Alaska Airlines at 1-800-252-7522. For booking questions with ${SW_BRAND}, call ${SW_PHONE_DISPLAY} or email ${SW_EMAIL}.`,
  },
];

/** Page copy for each airline in airlines.js, keyed by slug. */
export const AIRLINE_CONTENT = {
  southwest: {
    metaDescription: `Compare Southwest Airlines fares with ${SW_BRAND}. Review baggage rules, popular routes, and ticket policies, plus get 24/7 help at ${SW_PHONE_DISPLAY}.`,
    hero: SW_HERO,
    article: SW_ARTICLE,
    faqIntro: SW_FAQ_INTRO,
    faqs: SW_FAQS,
  },
  jetblue: {
    metaDescription: `Compare JetBlue Airways fares with ${SW_BRAND}. Review baggage rules, popular routes, and ticket policies, plus get 24/7 help at ${SW_PHONE_DISPLAY}.`,
    hero: JETBLUE_HERO,
    article: JETBLUE_ARTICLE,
    faqIntro: JETBLUE_FAQ_INTRO,
    faqs: JETBLUE_FAQS,
  },
  alaska: {
    metaDescription: `Compare Alaska Airlines fares with ${SW_BRAND}. Review baggage rules, popular routes, and ticket policies, plus get 24/7 help at ${SW_PHONE_DISPLAY}.`,
    hero: ALASKA_HERO,
    article: ALASKA_ARTICLE,
    faqIntro: ALASKA_FAQ_INTRO,
    faqs: ALASKA_FAQS,
  },
};
